import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { ConsultationModal } from "@/components/ConsultationModal";
import { Reveal, itemAnim, heroAnim } from "@/components/Reveal";

const IMG = {
  photographer: "https://images.unsplash.com/photo-1611002651516-019ca76848fa?auto=format&fit=crop&w=1200&q=80",
  bedroom: "https://images.unsplash.com/photo-1759472659432-3232e42d04d2?auto=format&fit=crop&w=1200&q=80",
  phone: "https://images.unsplash.com/photo-1618552304590-3f122d2d1200?auto=format&fit=crop&w=1200&q=80",
  laptop: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1200&q=80",
  living: "https://images.unsplash.com/photo-1767680675696-e065b32dac89?auto=format&fit=crop&w=1400&q=85",
};

const copy = {
  en: {
    nav: ["Our approach", "Services", "Estates", "About"],
    contact: "Talk to our team",
    eyebrow: "BRAND, CONTENT & MARKETING",
    heroTitle: <>Give your villa a voice, <em>and an audience.</em></>,
    heroText: "Beautiful villas don’t sell themselves online. We create the photos, stories and campaigns that make guests stop scrolling, and the visibility that brings them back to book.",
    seeList: "See what we do",
    servicesEyebrow: "WHAT WE DO",
    services: [
      ["Professional photography", "A professional photographer captures your villa at its best: golden-hour exteriors, styled interiors, pool and lifestyle shots. Every room is prepared and styled before the shoot, so your listing photos match the experience guests will have.", IMG.photographer],
      ["SEO & search visibility", "Optimised pages for every villa on edenhospitalitybali.com, with the right keywords, descriptions and Google Business presence, so travellers searching for villas in Bali find you directly, not only through booking platforms.", IMG.laptop],
      ["Social media & Instagram", "Your villa featured on the Eden Hospitality Instagram and social channels, with reels, stories and posts that show the space, the area and the lifestyle. Content that builds desire and drives direct bookings.", IMG.phone],
      ["OTA platform campaigns", "Smart use of promotions on Airbnb, Booking.com, Agoda and Expedia: early-bird, last-minute and long-stay offers, seasonal campaigns and platform programmes where eligible, timed to fill gaps in your calendar without underselling your villa.", IMG.bedroom],
    ],
    howEyebrow: "HOW IT WORKS",
    how: [
      ["Shape the story", "We define what makes your villa special and who it’s for."],
      ["Create the content", "Styling, photography, listing copy and social content, all in one consistent look."],
      ["Launch and optimise", "We publish across channels, run campaigns and track what works, reported in your monthly Eden Report."],
    ],
    whyEyebrow: "WHY IT MATTERS FOR OWNERS",
    whyTitle: <>Seen by more guests. <em>Booked</em> at better rates.</>,
    whyPoints: [
      "Great photos are the first thing guests judge, and the main reason they click.",
      "Search and social visibility grows your share of direct bookings, with no platform commission.",
      "Well-timed campaigns fill quiet dates while protecting your nightly rate in high season.",
      "One consistent brand across every channel builds trust before guests arrive.",
    ],
    ctaTitle: <>Ready to show your villa <br /><em>in its best light?</em></>,
    ctaText: "Tell us about your property. We’ll show you how we’d tell its story.",
    footer: "Keeping Bali’s finest estates in their best light.",
    footerDist: "Distribution Channels",
  },
  id: {
    nav: ["Pendekatan kami", "Layanan", "Properti", "Tentang"],
    contact: "Hubungi tim kami",
    eyebrow: "BRAND, KONTEN & PEMASARAN",
    heroTitle: <>Berikan villa Anda sebuah suara, <em>dan audiensnya.</em></>,
    heroText: "Villa yang indah tidak menjual dirinya sendiri secara online. Kami menciptakan foto, cerita, dan kampanye yang membuat tamu berhenti menggulir — dan visibilitas yang membuat mereka kembali untuk memesan.",
    seeList: "Lihat yang kami lakukan",
    servicesEyebrow: "YANG KAMI LAKUKAN",
    services: [
      ["Fotografi profesional", "Fotografer profesional menangkap villa Anda dalam versi terbaiknya: eksterior golden hour, interior tertata, kolam, dan gaya hidup. Setiap ruang disiapkan dan ditata sebelum pemotretan, sehingga foto listing sesuai dengan pengalaman yang akan tamu rasakan.", IMG.photographer],
      ["SEO & visibilitas pencarian", "Halaman yang dioptimalkan untuk setiap villa di edenhospitalitybali.com, dengan kata kunci, deskripsi, dan kehadiran Google Business yang tepat — wisatawan yang mencari villa di Bali menemukan Anda secara langsung, bukan hanya lewat platform booking.", IMG.laptop],
      ["Media sosial & Instagram", "Villa Anda tampil di Instagram dan kanal sosial Eden Hospitality, dengan reels, stories, dan post yang menampilkan ruang, area, dan gaya hidupnya. Konten yang membangun keinginan dan mendorong pemesanan langsung.", IMG.phone],
      ["Kampanye platform OTA", "Penggunaan promosi yang cerdas di Airbnb, Booking.com, Agoda, dan Expedia: penawaran early-bird, last-minute, dan long-stay, kampanye musiman, serta program platform yang memenuhi syarat — diatur waktunya untuk mengisi celah kalender tanpa menjual murah villa Anda.", IMG.bedroom],
    ],
    howEyebrow: "CARA KERJANYA",
    how: [
      ["Bentuk ceritanya", "Kami menentukan apa yang membuat villa Anda istimewa dan untuk siapa."],
      ["Ciptakan kontennya", "Styling, fotografi, copy listing, dan konten sosial — semua dalam satu tampilan yang konsisten."],
      ["Luncurkan dan optimalkan", "Kami menerbitkan di semua kanal, menjalankan kampanye, dan melacak apa yang berhasil — dilaporkan dalam Eden Report bulanan Anda."],
    ],
    whyEyebrow: "MENGAPA PENTING BAGI PEMILIK",
    whyTitle: <>Dilihat lebih banyak tamu. Dipesan dengan <em>tarif lebih baik.</em></>,
    whyPoints: [
      "Foto yang hebat adalah hal pertama yang dinilai tamu — dan alasan utama mereka mengklik.",
      "Visibilitas pencarian dan sosial menumbuhkan porsi pemesanan langsung Anda, tanpa komisi platform.",
      "Kampanye yang tepat waktu mengisi tanggal sepi sambil menjaga tarif per malam Anda di musim ramai.",
      "Satu brand yang konsisten di setiap kanal membangun kepercayaan sebelum tamu tiba.",
    ],
    ctaTitle: <>Siap menampilkan villa Anda <br /><em>dalam cahaya terbaiknya?</em></>,
    ctaText: "Ceritakan properti Anda. Kami akan tunjukkan bagaimana kami akan menceritakan kisahnya.",
    footer: "Menjaga villa terbaik Bali dalam cahaya terbaiknya.",
    footerDist: "Kanal Distribusi",
  },
};

export default function Marketing({ lang, setLang }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(0);
  const t = copy[lang];

  useEffect(() => {
    document.title = "Brand, Content & Marketing · Eden Hospitality Bali";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Professional villa photography, SEO, Instagram and social content, and OTA campaigns that help Bali villas get seen and booked at better rates.");
  }, []);

  return <div className="site-shell">
    <Nav lang={lang} setLang={setLang} onConsult={() => setModalOpen(true)} labels={t.nav} ctaLabel={t.contact} />

    <main id="top">
      <section className="conc-hero" data-testid="marketing-hero">
        <motion.p className="eyebrow gold" {...heroAnim(0.2)}>{t.eyebrow}</motion.p>
        <motion.h1 data-testid="marketing-heading" {...heroAnim(0.35)}>{t.heroTitle}</motion.h1>
        <motion.p className="sub" {...heroAnim(0.55)}>{t.heroText}</motion.p>
        <motion.div className="conc-hero-actions" {...heroAnim(0.75)}>
          <button className="button button-terra" onClick={() => setModalOpen(true)} data-testid="marketing-hero-cta">{t.contact} <ArrowUpRight size={16} /></button>
          <button className="button button-outline-light" onClick={() => document.getElementById("mkt-services")?.scrollIntoView({ behavior: "smooth" })} data-testid="marketing-services-button">{t.seeList} <ArrowUpRight size={16} /></button>
        </motion.div>
      </section>

      <section id="mkt-services" data-testid="marketing-services">
        <div className="conc-split">
          <Reveal>
            <p className="eyebrow">{t.servicesEyebrow}</p>
            <div>
              {t.services.map(([title, text, img], i) => (
                <div className="conc-item" key={title} onMouseEnter={() => setActive(i)} data-testid={`mkt-item-${i}`}>
                  <span>0{i + 1}</span>
                  <div className="conc-item-body"><strong>{title}</strong><p>{text}</p></div>
                  <ArrowUpRight size={17} />
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="conc-photo" data-testid="marketing-photo">
              {t.services.map(([title, , img], i) => (
                <div key={title} className={`ph ${active === i ? "active" : ""}`} style={{ backgroundImage: `url(${img})` }} data-testid={`mkt-photo-${i}`} />
              ))}
              <figcaption data-testid="marketing-photo-caption">{t.services[active][0]}</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="conc-how" data-testid="marketing-how">
        <Reveal><p className="eyebrow">{t.howEyebrow}</p></Reveal>
        <div className="conc-how-grid">
          {t.how.map(([title, text], i) => (
            <motion.div className="conc-how-col" key={title} data-testid={`mkt-how-${i}`} {...itemAnim(i)}>
              <h3>{title}</h3><p>{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="why" data-testid="marketing-why">
        <Reveal className="why-text">
          <p className="eyebrow">{t.whyEyebrow}</p>
          <h2>{t.whyTitle}</h2>
          <ul className="why-list">
            {t.whyPoints.map((point, i) => <li key={i} data-testid={`mkt-why-point-${i}`}><Check size={15} />{point}</li>)}
          </ul>
        </Reveal>
        <Reveal className="why-photo" delay={0.15}><div className="arch-photo" style={{ backgroundImage: `url(${IMG.living})` }} data-testid="marketing-arch-photo" /></Reveal>
      </section>

      <section className="closing section-dark" data-testid="marketing-cta">
        <Reveal className="closing-content">
          <p className="eyebrow gold">EDEN HOSPITALITY</p>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <button className="button button-light" onClick={() => setModalOpen(true)} data-testid="marketing-closing-cta">{t.contact} <ArrowUpRight size={17} /></button>
        </Reveal>
        <div className="closing-orb" />
      </section>
    </main>

    <Footer footerText={t.footer} contactLabel={t.contact} distLabel={t.footerDist} onConsult={() => setModalOpen(true)} />
    <ConsultationModal lang={lang} open={modalOpen} onClose={() => setModalOpen(false)} ctaLabel={t.contact} />
  </div>;
}
